import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../Context/AuthContext";
import { FaEye, FaEyeSlash, FaSync } from "react-icons/fa";
import "./Register.css";
import { API_BASE } from "../Services/Api";

const LoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [apiError, setApiError] = useState("");

  // CAPTCHA
  const [captchaText, setCaptchaText] = useState("");
  const canvasRef = useRef(null);

  const generateCaptchaText = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let text = "";
    for (let i = 0; i < 6; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return text;
  };

  const drawCaptcha = (text) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const width = 140;
    const height = 40;

    canvas.width = width;
    canvas.height = height;

    ctx.fillStyle = "#f2f2f2";
    ctx.fillRect(0, 0, width, height);

    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${
        Math.random() * 255
      }, 0.6)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    ctx.font = "bold 24px sans-serif";
    ctx.fillStyle = "#333";
    const spacing = 20;

    for (let i = 0; i < text.length; i++) {
      const x = 10 + i * spacing;
      const y = 25 + Math.random() * 5;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((Math.random() - 0.5) * 0.6);
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }

    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${
        Math.random() * 255
      }, 0.6)`;
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const generateCaptcha = () => {
    const text = generateCaptchaText();
    setCaptchaText(text);
    drawCaptcha(text);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    if (!forgotPassword) {
      setTimeout(generateCaptcha, 100);
    }
  }, [forgotPassword]);

  const initialValues = {
    username: "",
    password: "",
    captchaInput: "",
    email: "",
  };

  const validationSchema = Yup.object().shape({
    username: forgotPassword ? Yup.string() : Yup.string().required("Username is required"),
    password: forgotPassword ? Yup.string() : Yup.string().required("Password is required"),
    captchaInput: forgotPassword
      ? Yup.string()
      : Yup.string()
          .required("Captcha is required")
          .test("captcha-match", "Captcha incorrect", function (value) {
            return value?.toLowerCase() === captchaText.toLowerCase();
          }),

    email: forgotPassword
      ? Yup.string().email("Invalid email").required("Email required")
      : Yup.string(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log(values.email);
    
    setApiError("");

    if (forgotPassword) {
      try {
        const response = await fetch(`${API_BASE}/reset-link`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: values.email }),
        });

        const data = await response.json();

        if (data.status === "success") {
          toast.success(data.message);
          setForgotPassword(false);
        } else {
          toast.error(data.message || "Failed to send reset link");
          setApiError(data.message);
        }
      } catch (error) {
        toast.error("Network error");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // LOGIN API
    try {
      await login(values);
      navigate("/");
    } catch (error) {
      toast.error(error.message || "Invalid credentials");
    }

    setSubmitting(false);
  };

  return (
    <>
      <title>EDC - Login</title>

      <div className="main-login-flex">
        <div className="circle-form">
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ isSubmitting }) => (
              <Form>
                <div className="flex justify-center">
                  <img src="/images/LSClogo.png" alt="Logo" className="h-20 w-auto" />
                </div>

                <h1 className="my-5" style={{ textAlign: "center", color: "#5D4CCF", fontWeight: "700", fontSize: "25px" }}>
                  Executive Development Center
                </h1>

                {forgotPassword ? (
                  <>
                    <div>
                      <Field type="email" name="email" placeholder="Enter your email" />
                      <ErrorMessage name="email" component="div" className="error" style={{ color: "red" }} />
                    </div>

                    <button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? "Sending..." : "Send Reset Link"}
                    </button>

                    <p className="m-3">
                      <span onClick={() => setForgotPassword(false)} style={{ color: "#5D4CCF", cursor: "pointer" }}>
                        Back to Login
                      </span>
                    </p>
                  </>
                ) : (
                  <>
                    {/* Username */}
                    <div>
                      <Field type="text" name="username" placeholder="Username" />
                      <ErrorMessage name="username" component="div" className="error" style={{ color: "red" }} />
                    </div>

                    {/* Password */}
                    <div className="my-3 relative">
                      <Field type={showPassword ? "text" : "password"} name="password" placeholder="Password" />

                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#5D4CCF",
                        }}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>

                      <ErrorMessage name="password" component="div" className="error" style={{ color: "red" }} />
                    </div>

                    {/* CAPTCHA */}
                    <div className="captcha-block" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <canvas ref={canvasRef} className="captcha-canvas" />

                      {/* 🔄 Refresh Icon Button */}
                      <button className="m-5"
                        type="button"
                        onClick={generateCaptcha}
                        style={{
                        width:"50px",
                         backgroundColor: "transparent",

                          border: "none",
                          padding: "8px",
                          // borderRadius: "50%",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <FaSync size={18} color="#5D4CCF" />
                      </button>
                    </div>

                    <Field type="text" name="captchaInput" placeholder="Enter captcha" />
                    <ErrorMessage name="captchaInput" component="div" className="error" style={{ color: "red" }} />

                    {/* Forgot Password */}
                    <div className="flex justify-end">
                      <span
                        onClick={() => setForgotPassword(true)}
                        className="text-sm hover:underline"
                        style={{ color: "#5D4CCF", cursor: "pointer" }}
                      >
                        Forgot password?
                      </span>
                    </div>

                    <button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? "Logging in..." : "Login"}
                    </button>

                    <p className="m-3">
                      Don't have an account?
                      <Link to="/register" className="mx-3" style={{ color: "#5D4CCF" }}>
                        Sign Up
                      </Link>
                    </p>
                  </>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default LoginForm;
